import { computePartsAmount, BomList, BomCalculator, ConfiguratorPV, hasAttributeValue, VariantProperties, getAttributeValue, createAttributeValue } from '@canvas-logic/clp-domain';
import { LogicEngineBuilder, ConfiguratorToDomainProductMapper } from '@canvas-logic/clp-bridges';

class InvalidConfiguration extends Error {
    invalidRules;
    emptyRequiredComponents;
    constructor(invalidRules, emptyRequiredComponents) {
        super('Invalid configuration');
        this.invalidRules = invalidRules;
        this.emptyRequiredComponents = emptyRequiredComponents;
    }
}

const uiFallBack = {
    localization: 'en',
    layout: {
        controlsHidden: false,
    },
};
const view3dFallBack = {
    views: [],
    hierarchy: {
        root: {
            partId: 'empty',
            children: [],
        },
    },
    camera: {},
    parts: [
        {
            id: 'empty',
            view: {
                selector: 'view',
                match: {
                    '*': '*',
                },
            },
            materials: [],
        },
    ],
    materials: [],
};
class Fetcher {
    url;
    constructor(url) {
        this.url = this.formatUrl(url);
    }
    async getBomDetails(params) {
        const bomParams = { ...params };
        bomParams.catalogId = undefined;
        const response = await fetch(`${this.url}/products/${params.productId}/composite-bom-data/${params.catalogId}?${this.getSearchParams(bomParams)}`);
        return response.json();
    }
    async getShortLink(params, draft = false) {
        const response = await fetch(`${this.url}/short-link?version=${draft ? 'draft' : 'active'}`, {
            method: 'POST',
            body: JSON.stringify(params),
            headers: {
                'content-type': 'application/json',
            },
        });
        return response.text();
    }
    async getProductSchema(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/schema?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getDefaultProductVariant(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/default-product-variant?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getProductVariants(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/product-variants?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getCompositeProductSchema(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-schema?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getCompositeProductVariants(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-product-variants?${this.getSearchParams(params)}`);
        const data = await response.json();
        for (const [, products] of Object.entries(data.components)) {
            for (const value of products) {
                (value?.variants ?? []).sort((a, b) => {
                    return a.variantId - b.variantId;
                });
            }
        }
        return data;
    }
    async getInitialCompositeProductVariant(params, initialVariant) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-initial-variant?${this.getSearchParams(params)}${initialVariant ? `&initialVariant=${JSON.stringify(initialVariant)}` : ''}`);
        return response.json();
    }
    formatUrl(url) {
        return url.endsWith('/') ? url + 'configurator' : url + '/configurator';
    }
    async getCompositeData(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-data?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getCompositeUiSpecification(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-ui-specification?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getCompositeView3dSpecification(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/composite-view3d-specification?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getProductUiSpecification(_params) {
        return uiFallBack;
    }
    async getProductView3dSpecification(_params) {
        return view3dFallBack;
    }
    async getContactUsFeatureSchema(params) {
        const response = await fetch(`${this.url}/contact-us?${this.getSearchParams(params)}`);
        return response.json();
    }
    async getPricingFeatureSchema(params) {
        const response = await fetch(`${this.url}/pricing?${this.getSearchParams(params)}`);
        return response.json();
    }
    async requestQuote(params, formData) {
        const response = await fetch(`${this.url}/request-quote?${this.getSearchParams(params)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
        });
        if (!response.ok) {
            const parsed = await response.json();
            throw new Error(parsed.message);
        }
        return response.json();
    }
    async getHqrConfig(params) {
        const response = await fetch(`${this.url}/products/${params.productId}/hqr-config?${this.getSearchParams(params)}`);
        return response.json();
    }
    getSearchParams(params) {
        const searchParams = new URLSearchParams();
        if (params.version) {
            searchParams.set('version', String(params.version));
        }
        if ('catalogId' in params && params.catalogId) {
            searchParams.set('catalogId', String(params.catalogId));
        }
        if (params.link) {
            searchParams.set('link', params.link);
        }
        if (params.configurationId) {
            searchParams.set('configurationId', params.configurationId);
        }
        return searchParams.toString();
    }
}

class JsonFetcher {
    url;
    constructor(url) {
        this.url = this.formatUrl(url);
    }
    getBomDetails(_params) {
        throw new Error('Method not implemented.');
    }
    async getShortLink(_params) {
        return '';
    }
    async getContactUsFeatureSchema(_params) {
        throw Error('getContactUsFeatureSchema not implemented for JsonFetcher');
    }
    async getPricingFeatureSchema(_params) {
        throw Error('getContactUsFeatureSchema not implemented for JsonFetcher');
    }
    async requestQuote(_params, _formData) {
        throw Error('requestQuote not implemented for JsonFetcher');
    }
    async getProductSchema(params) {
        return this.fetch(`${this.url}/products/${params.productId}/schema.json`);
    }
    async getDefaultProductVariant(params) {
        return this.fetch(`${this.url}/products/${params.productId}/default_variant.json`);
    }
    async getProductVariants(params) {
        return this.fetch(`${this.url}/products/${params.productId}/variants.json`);
    }
    async getCompositeProductSchema(params) {
        return this.fetch(`${this.url}/composites/${params.productId}/schema.json`);
    }
    async getInitialCompositeProductVariant(params) {
        return this.fetch(`${this.url}/composites/${params.productId}/default_variant.json`);
    }
    async getCompositeProductVariants(params) {
        const compositeVariants = await this.fetch(`${this.url}/composites/${params.productId}/variants.json`);
        const result = {
            rules: compositeVariants.rules ?? [],
            components: {},
        };
        const requests = [];
        for (const [componentKey, componentVariants] of Object.entries(compositeVariants.components)) {
            result.components[componentKey] = [];
            for (const productId of componentVariants) {
                if (productId === null) {
                    result.components[componentKey].push(null);
                }
                else {
                    const promise = this.fetch(`${this.url}/products/${productId}/variants.json`).then((productVariants) => {
                        for (const productVariant of productVariants) {
                            result.components[componentKey].push(productVariant);
                        }
                    });
                    requests.push(promise);
                }
            }
        }
        await Promise.all(requests);
        return result;
    }
    async getProductUiSpecification(params) {
        return this.fetch(`${this.url}/products/${params.productId}/ui.json`);
    }
    async getCompositeUiSpecification(params) {
        return this.fetch(`${this.url}/composites/${params.productId}/ui.json`);
    }
    getProductView3dSpecification(params) {
        return this.fetch(`${this.url}/products/${params.productId}/view3d.json`);
    }
    getCompositeView3dSpecification(params) {
        return this.fetch(`${this.url}/composites/${params.productId}/view3d.json`);
    }
    async getCompositeData(_params) {
        throw new Error('Method not implemented.');
    }
    async getHqrConfig(params) {
        try {
            return await this.fetch(`${this.url}/composites/${params.productId}/hqr-config.json`);
        }
        catch {
            return { enabled: false, defaultImage: null, config: [] };
        }
    }
    async fetch(url) {
        const response = await fetch(url);
        return response.json();
    }
    formatUrl(url) {
        return url.endsWith('/') ? url + 'data' : url + '/data';
    }
}

function pureObject(object, skipProps = []) {
    const entries = [];
    for (const [prop, value] of Object.entries(object)) {
        if (prop.startsWith('_') && !skipProps.includes(prop)) {
            continue;
        }
        if (typeof object[prop] === 'object' && object[prop] !== null) {
            entries.push([prop, pureObject(value, skipProps)]);
        }
        else {
            entries.push([prop, value]);
        }
    }
    return Object.fromEntries(entries);
}
function createVariantId(prefix, id) {
    return prefix + '_' + id.toString();
}
function createAttributeId(productComponentKey, attributeKey) {
    return `${productComponentKey}.${attributeKey}`;
}

class CompositeProductApi {
    options;
    fetcher;
    defaultValidationOptions;
    modelRef;
    compositeProduct;
    attributes;
    productComponentsApi;
    productComponentsApiByComponent;
    bomDetails;
    awaitInjection;
    productData;
    itemOrder;
    rawVariants;
    isInCatalog;
    projectId;
    draft;
    compositeProductVariants;
    dataVariantsLoaded = false;
    rules;
    _logicEngine = null;
    _meta = {};
    _metaChangeCallbacks = [];
    _modelChangeCallbacks = [];
    _componentChangeCallbacks = new Map();
    _attributeChangeCallbacks = new Map();
    constructor(options, fetcher, defaultValidationOptions, modelRef, compositeProduct, attributes, productComponentsApi, productComponentsApiByComponent, bomDetails, awaitInjection, productData, itemOrder, rawVariants) {
        this.options = options;
        this.fetcher = fetcher;
        this.defaultValidationOptions = defaultValidationOptions;
        this.modelRef = modelRef;
        this.compositeProduct = compositeProduct;
        this.attributes = attributes;
        this.productComponentsApi = productComponentsApi;
        this.productComponentsApiByComponent = productComponentsApiByComponent;
        this.bomDetails = bomDetails;
        this.awaitInjection = awaitInjection;
        this.productData = productData;
        this.itemOrder = itemOrder;
        this.rawVariants = rawVariants;
        this.isInCatalog = 'catalogId' in options && options.catalogId !== undefined;
        this.projectId = options.projectId !== undefined ? String(options.projectId) : '';
        this.draft = options.version === 'draft';
        this.awaitInjection.then(({ logicEngine, compositeProductVariants, rules }) => {
            this._logicEngine = logicEngine;
            this.compositeProductVariants = compositeProductVariants;
            this.rules = rules;
            this.dataVariantsLoaded = true;
        });
    }
    async awaitLogicEngine() {
        await this.awaitInjection;
    }
    getComponentVariant(componentKey) {
        return this.modelRef.model[componentKey];
    }
    get logicEngine() {
        if (!this._logicEngine) {
            throw new Error('Logic engine should be injected before using');
        }
        return this._logicEngine;
    }
    get componentsKey() {
        return Object.keys(this.modelRef.model);
    }
    loadAllAttributeValues(componentKey, attributeKey) {
        const attributeId = createAttributeId(componentKey, attributeKey);
        return this.getComponentApi(componentKey).loadAllAttributeValues(attributeId);
    }
    getProductId() {
        return this.compositeProduct.productId;
    }
    getName() {
        return this.compositeProduct.productName;
    }
    getProductKey() {
        return this.compositeProduct.productKey;
    }
    getProduct() {
        return this.compositeProduct;
    }
    getComponentSchema(componentKey) {
        return this.compositeProduct.productType.components.find((it) => it.key === componentKey);
    }
    getAvailableComponentsKeys() {
        return this.productComponentsApi.filter((it) => it.isAvailable()).map((it) => it.getKey());
    }
    async getPrice() {
        const prices = await Promise.all(this.productComponentsApi.map((api) => api.getPrice()));
        const getPartsAmount = computePartsAmount(this.compositeProduct.parts, this.getAvailableComponentsKeys());
        const total = {};
        for (let i = 0; i < prices.length; i++) {
            const price = prices[i];
            const componentKey = this.productComponentsApi[i].getKey();
            for (const currency of this.compositeProduct.catalogMeta.currencies) {
                if (!(currency in total)) {
                    total[currency] = 0;
                }
                const amount = price[currency] ?? 0;
                const partsAmount = getPartsAmount(componentKey);
                total[currency] += amount * 100 * partsAmount;
            }
        }
        for (const currency of this.compositeProduct.catalogMeta.currencies) {
            total[currency] /= 100;
        }
        return total;
    }
    async getMetaIds() {
        const ids = await Promise.all(this.productComponentsApi.map((api) => api.getMetaId()));
        const bomList = new BomList(this.bomDetails);
        this.addPartsMetaIds(ids, bomList);
        this.addBomMetaIds(bomList);
        const result = [];
        for (const [id, amount] of bomList.getBomRecord().entries()) {
            result.push({
                [this.compositeProduct.catalogMeta.metaId]: id,
                amount: amount,
            });
        }
        return result;
    }
    addBomMetaIds(bomList) {
        const bomCalculator = new BomCalculator(this.compositeProduct.bom);
        bomCalculator.execute({
            product: this.modelRef.model,
        }, bomList);
    }
    addPartsMetaIds(ids, bomList) {
        const getPartsAmount = computePartsAmount(this.compositeProduct.parts, this.getAvailableComponentsKeys());
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const componentKey = this.productComponentsApi[i].getKey();
            if (id === '') {
                continue;
            }
            bomList.addExternalBomItem(id, getPartsAmount(componentKey));
        }
    }
    getProductComponents() {
        return this.productComponentsApi;
    }
    getProductComponent(componentKey) {
        return this.productComponentsApi.find((api) => api.getKey() === componentKey);
    }
    getVariant() {
        return this.modelRef.model;
    }
    getProductData() {
        return this.productData.product;
    }
    getComponentProductData(componentKey) {
        return this.getComponentApi(componentKey).getProductData();
    }
    getComponentAllProducts(componentKey) {
        const componentData = this.productData.components[componentKey];
        return componentData.products;
    }
    getComponentTextData(componentKey) {
        const componentData = this.productData.components[componentKey];
        return componentData.texts;
    }
    getComponentAttributesData(componentKey) {
        return this.productData.components[componentKey].attributes;
    }
    getComponentAttributeData(componentKey, attributeKey) {
        return this.productData.components[componentKey].attributes[attributeKey];
    }
    getComponentsData() {
        const result = {};
        for (const componentKey of this.componentsKey) {
            result[componentKey] = this.getComponentProductData(componentKey);
        }
        return result;
    }
    getComponentApi(componentKey) {
        const api = this.productComponentsApiByComponent.get(componentKey);
        if (!api) {
            throw new Error(`Invalid component key "${componentKey}"`);
        }
        return api;
    }
    getValue() {
        return pureObject(this.modelRef.model);
    }
    getRawValue() {
        const result = {};
        for (const [key, value] of Object.entries(this.modelRef.model)) {
            const v = new ConfiguratorPV(value);
            if (v.isAvailable()) {
                result[key] = pureObject(value);
            }
            else {
                result[key] = null;
            }
        }
        return result;
    }
    getAttributeValue(componentKey, attributeKey) {
        const attributeId = createAttributeId(componentKey, attributeKey);
        return this.getComponentApi(componentKey).getAttributeValue(attributeId);
    }
    isAvailable(componentKey) {
        return this.getComponentApi(componentKey).isAvailable();
    }
    isEnable(componentKey) {
        return this.getComponentApi(componentKey).isEnabled();
    }
    loadAvailability(componentKey) {
        return this.getComponentApi(componentKey).loadAvailability();
    }
    loadCompatibleAttributeValues(componentKey, attributeKey) {
        const attributeId = createAttributeId(componentKey, attributeKey);
        return this.getComponentApi(componentKey).loadCompatibleAttributeValues(attributeId);
    }
    async loadAllCompatibleAttributeValues() {
        const result = {};
        for (const component of this.productComponentsApi) {
            result[component.getKey()] = await component.loadAllCompatibleAttributeValues();
        }
        return result;
    }
    setAttributeValue(componentKey, attributeKey, attributeValue) {
        const attributeId = createAttributeId(componentKey, attributeKey);
        return this.getComponentApi(componentKey).setAttributeValue(attributeId, attributeValue);
    }
    setAvailability(componentKey, available) {
        return this.getComponentApi(componentKey).setAvailability(available);
    }
    getAttributes() {
        return this.attributes.filter((it) => !it.attributeKey.startsWith('_'));
    }
    getItemOrders() {
        return this.itemOrder;
    }
    async createLink() {
        if (!this.projectId) {
            throw new Error('projectId is required for createLink. Ensure projectId is provided in SDK initialization options.');
        }
        const serializedModel = this.serialize(1);
        const linkParams = {
            projectId: Number(this.projectId),
            productId: this.getProductId(),
            catalogId: 'catalogId' in this.options ? Number(this.options.catalogId) : undefined,
            model: serializedModel,
            meta: this._meta,
        };
        return await this.fetcher.getShortLink(linkParams, this.draft);
    }
    serialize(version) {
        const model = this.modelRef.model;
        const serializedComponents = {};
        const custom = {};
        for (const [componentKey, variant] of Object.entries(model)) {
            this.serializeVariant(variant, componentKey, serializedComponents, custom);
        }
        return {
            version,
            custom: custom,
            components: serializedComponents,
        };
    }
    serializeVariant(variant, componentKey, serializedComponents, custom) {
        const v = new ConfiguratorPV(variant);
        const available = v.isAvailable();
        if (!available) {
            serializedComponents[componentKey] = null;
            return;
        }
        const productId = v.productId;
        const id = v.variantId;
        for (const [attributeKey, attributeValue] of Object.entries(variant)) {
            if ('custom' in attributeValue && hasAttributeValue(attributeValue)) {
                if (!custom[componentKey]) {
                    custom[componentKey] = {};
                }
                custom[componentKey][attributeKey] = attributeValue.value;
            }
        }
        serializedComponents[componentKey] = {
            id: Number(id),
            productId: Number(productId),
        };
    }
    #getInvalidRules(logicEngine, validationOptions) {
        const errors = logicEngine.validate(this.modelRef.model, validationOptions || this.defaultValidationOptions);
        const errorsSet = new Set(errors.invalidRules);
        const invalidRules = [];
        for (const rule of this.rules) {
            if (errorsSet.has(rule.name)) {
                invalidRules.push(rule);
            }
        }
        return invalidRules;
    }
    getInvalidRules(validationOptions) {
        return this.#getInvalidRules(this.logicEngine, validationOptions);
    }
    validate(validationOptions) {
        return this.logicEngine.validate(this.modelRef.model, validationOptions || this.defaultValidationOptions);
    }
    async getFirstCompatibleVariantIgnoringRules() {
        const { logicEngine } = await this.awaitInjection;
        return logicEngine.getFirstCompatibleVariantIgnoringRules();
    }
    getComponentPartsAmount(componentKey) {
        const getPartsAmount = computePartsAmount(this.compositeProduct.parts, this.getAvailableComponentsKeys());
        return getPartsAmount(componentKey);
    }
    isComponentInPdf(componentKey) {
        const cmp = this.productComponentsApi.find((it) => it.getKey() === componentKey);
        if (!cmp) {
            throw new Error(`Cannot find component with key '${componentKey}'`);
        }
        return cmp.isIncludedInPdf();
    }
    getMeta() {
        return this._meta;
    }
    setMeta(meta) {
        const oldMeta = this._meta;
        this._meta = meta;
        if (oldMeta !== meta) {
            this._metaChangeCallbacks.forEach((callback) => callback(meta));
        }
    }
    onMetaChange(callback) {
        this._metaChangeCallbacks.push(callback);
        return () => {
            const index = this._metaChangeCallbacks.indexOf(callback);
            if (index > -1) {
                this._metaChangeCallbacks.splice(index, 1);
            }
        };
    }
    onModelChange(callback) {
        this._modelChangeCallbacks.push(callback);
        return () => {
            const index = this._modelChangeCallbacks.indexOf(callback);
            if (index > -1) {
                this._modelChangeCallbacks.splice(index, 1);
            }
        };
    }
    onComponentChange(componentKey, callback) {
        if (!this._componentChangeCallbacks.has(componentKey)) {
            this._componentChangeCallbacks.set(componentKey, []);
        }
        const callbacks = this._componentChangeCallbacks.get(componentKey);
        callbacks.push(callback);
        return () => {
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        };
    }
    onAttributeChange(componentKey, attributeKey, callback) {
        const key = `${componentKey}:${attributeKey}`;
        if (!this._attributeChangeCallbacks.has(key)) {
            this._attributeChangeCallbacks.set(key, []);
        }
        const callbacks = this._attributeChangeCallbacks.get(key);
        callbacks.push(callback);
        return () => {
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        };
    }
    getAllVariants() {
        const result = [];
        for (const componentVariantLists of Object.values(this.rawVariants.components)) {
            for (const variantList of componentVariantLists) {
                if (!variantList) {
                    continue;
                }
                for (const variant of variantList.variants) {
                    result.push({
                        productKey: variant.productKey,
                        meta: {
                            id: variant.meta.id,
                            price: variant.meta.price,
                        },
                        model3dId: variant.model3dId,
                        attributes: variant.attributes,
                    });
                }
            }
        }
        return result;
    }
    notifyModelChange(previousModel, newModel) {
        let isChangeDetected = false;
        const allComponentKeys = new Set([...Object.keys(previousModel), ...Object.keys(newModel)]);
        for (const componentKey of allComponentKeys) {
            const previousVariant = previousModel[componentKey];
            const newVariant = newModel[componentKey];
            const componentChanged = !this.areVariantsEqual(previousVariant, newVariant);
            if (componentChanged) {
                isChangeDetected = true;
                const componentCallbacks = this._componentChangeCallbacks.get(componentKey);
                if (componentCallbacks) {
                    componentCallbacks.forEach((callback) => callback(componentKey, newVariant));
                }
                if (previousVariant && newVariant) {
                    const allAttributeKeys = new Set([...Object.keys(previousVariant), ...Object.keys(newVariant)]);
                    for (const attributeKey of allAttributeKeys) {
                        const previousValue = previousVariant[attributeKey];
                        const newValue = newVariant[attributeKey];
                        if (!this.areAttributeValuesEqual(previousValue, newValue)) {
                            const key = `${componentKey}:${attributeKey}`;
                            const attributeCallbacks = this._attributeChangeCallbacks.get(key);
                            if (attributeCallbacks) {
                                attributeCallbacks.forEach((callback) => callback(componentKey, attributeKey, newValue));
                            }
                        }
                    }
                }
            }
        }
        if (isChangeDetected) {
            this._modelChangeCallbacks.forEach((callback) => callback(newModel));
        }
    }
    areVariantsEqual(v1, v2) {
        if (v1 === v2) {
            return true;
        }
        if (!v1 || !v2) {
            return false;
        }
        const keys1 = Object.keys(v1);
        const keys2 = Object.keys(v2);
        if (keys1.length !== keys2.length)
            return false;
        for (const key of keys1) {
            if (!this.areAttributeValuesEqual(v1[key], v2[key])) {
                return false;
            }
        }
        return true;
    }
    areAttributeValuesEqual(v1, v2) {
        if (v1 === v2) {
            return true;
        }
        if (!v1 || !v2) {
            return false;
        }
        return JSON.stringify(v1) === JSON.stringify(v2);
    }
}
function createNullVariantId(typeName) {
    return typeName + '_null';
}

class BasicProductApi {
    id;
    componentKey;
    productTypeName;
    attributes;
    _modelRef;
    productName;
    defaultValidationOptions;
    _awaitLogicEngineReady = null;
    defaultVariantId;
    nullVariantId;
    _onModelChange = null;
    constructor(id, componentKey, productTypeName, attributes = [], _modelRef, productName, defaultValidationOptions) {
        this.id = id;
        this.componentKey = componentKey;
        this.productTypeName = productTypeName;
        this.attributes = attributes;
        this._modelRef = _modelRef;
        this.productName = productName;
        this.defaultValidationOptions = defaultValidationOptions;
        this.nullVariantId = createNullVariantId(productTypeName);
        this.defaultVariantId = id === undefined ? this.nullVariantId : this.toCompoundVariantId(id);
    }
    setModelChangeNotifier(notifier) {
        this._onModelChange = notifier;
    }
    injectLogicEngine(logicEngine) {
        this._awaitLogicEngineReady = logicEngine;
    }
    get awaitLogicEngine() {
        if (!this._awaitLogicEngineReady) {
            throw new Error('Logic engine should be injected before using');
        }
        return this._awaitLogicEngineReady;
    }
    get variant() {
        return new ConfiguratorPV(this._modelRef.model[this.componentKey]);
    }
    get parentCompositeProduct() {
        return this._modelRef.model;
    }
    getKey() {
        return this.componentKey;
    }
    getAttributeValue(attributeId) {
        const attribute = this.findAttributeById(attributeId);
        return this.variant.raw[attribute.key];
    }
    getAllAttributesValue() {
        return pureObject(this.variant.raw);
    }
    getAttributes() {
        return this.attributes;
    }
    getName() {
        return this.productName;
    }
    async getPrice() {
        if (this.variant.isNullish) {
            return {};
        }
        const compoundId = this.variant.compoundId;
        if (this.defaultVariantId !== compoundId) {
            await this.awaitLogicEngine;
        }
        const variantPrice = this.variant.meta.price;
        if (!variantPrice) {
            console.error(`Cannot find price for current variant "${compoundId}"`);
            return {};
        }
        return variantPrice;
    }
    async getMetaId() {
        if (this.variant.isNullish) {
            return '';
        }
        if (this.defaultVariantId !== this.variant.compoundId) {
            await this.awaitLogicEngine;
        }
        const metaId = this.variant.meta.id;
        if (!metaId) {
            console.error(`Cannot find metaId for current variant "${this.variant.compoundId}"`);
            return '';
        }
        return metaId;
    }
    getVariant() {
        return this.getAllAttributesValue();
    }
    async loadCompatibleAttributeValues(attributeId) {
        const attribute = this.findAttributeById(attributeId);
        return this.loadAttributeValues(attribute.key);
    }
    async loadAllAttributeValues(attributeId) {
        const logicEngine = await this.awaitLogicEngine;
        const attribute = this.findAttributeById(attributeId);
        return logicEngine.getAllValues(this.componentKey, attribute.key);
    }
    async loadAttributeValues(attributeKey) {
        const logicEngine = await this.awaitLogicEngine;
        const values = logicEngine.getCompatibleValues(this.parentCompositeProduct, this.componentKey, attributeKey, this.defaultValidationOptions);
        if (!values) {
            throw new Error(`Error in component ${this.componentKey}. Unknown attribute: ${attributeKey}`);
        }
        return values;
    }
    async loadAllCompatibleAttributeValues() {
        const attributeValues = {};
        for (const attribute of this.attributes) {
            attributeValues[attribute.key] = await this.loadAttributeValues(attribute.key);
        }
        return attributeValues;
    }
    async loadAllAttributesValues() {
        const attributeValues = {};
        for (const attribute of this.attributes) {
            attributeValues[attribute.key] = await this.loadAllAttributeValues(attribute.id);
        }
        return attributeValues;
    }
    async setAttributeValue(attributeId, attributeValue) {
        const attribute = this.findAttributeById(attributeId);
        return this.setAttributeValueByAttributeKey(attribute.key, attributeValue);
    }
    isAvailable() {
        return this.variant.isAvailable();
    }
    async setAttributeValueByAttributeKey(attributeKey, attributeValue) {
        const logicEngine = await this.awaitLogicEngine;
        const previousModel = this._modelRef.model;
        const model = logicEngine.setValue(this.parentCompositeProduct, this.componentKey, attributeKey, attributeValue, this.defaultValidationOptions);
        if (!model) {
            throw new Error(`Cannot set "${attributeValue}" for attribute "${attributeKey}" in component "${this.componentKey}"`);
        }
        this._modelRef.model = model;
        if (this._onModelChange) {
            this._onModelChange(previousModel, model);
        }
    }
    findAttributeById(attributeId) {
        const attribute = this.attributes.find((attribute) => attribute.id === attributeId);
        if (!attribute) {
            throw new Error(`Invalid attributeId "${attributeId}"`);
        }
        return attribute;
    }
    toCompoundVariantId(id) {
        return createVariantId(this.productTypeName, id);
    }
}

function validateProductLogicOrder(logicOrder, type) {
    if (!logicOrder.length) {
        throw new Error('Empty logic order');
    }
    const typeAttributes = new Set(type.attributes.map((attribute) => attribute.key));
    for (let i = 0; i < logicOrder.length; i++) {
        const { attributeKey } = logicOrder[i];
        if (attributeKey === '_productId') {
            continue;
        }
        if (attributeKey === undefined) {
            throw new Error(`Invalid item #${i + 1}. Missing "attributeKey"`);
        }
        if (typeof attributeKey !== 'string') {
            throw new Error(`Invalid item #${i + 1}. "attributeKey" must be a string`);
        }
        if (!typeAttributes.has(attributeKey)) {
            throw new Error(`Invalid attribute "${attributeKey}"`);
        }
    }
    const usedAttributes = new Set();
    for (const { attributeKey } of logicOrder) {
        if (usedAttributes.has(attributeKey)) {
            throw new Error(`Duplicated attribute "${attributeKey}" found`);
        }
        else {
            usedAttributes.add(attributeKey);
        }
    }
}
function getDefaultProductLogicOrder(type) {
    return type.attributes.map((attribute) => ({ attributeKey: attribute.key }));
}
function validateCompositeLogicOrder(logicOrder, type) {
    if (!logicOrder.length) {
        throw new Error('Empty logic order');
    }
    const typeComponents = new Set(type.components.map((item) => item.key));
    const components = new Set();
    const attributes = new Map();
    for (let i = 0; i < logicOrder.length; i++) {
        const item = logicOrder[i];
        if (item.componentKey === undefined) {
            throw new Error(`Invalid item #${i + 1}. Missing "componentKey"`);
        }
        if (typeof item.componentKey !== 'string') {
            throw new Error(`Invalid item #${i + 1}. "componentKey" must be a string`);
        }
        if (!typeComponents.has(item.componentKey)) {
            throw new Error(`Invalid component "${item.componentKey}"`);
        }
        switch (item.type) {
            case 'availability':
                if (components.has(item.componentKey)) {
                    throw new Error(`Duplicated component "${item.componentKey}" found`);
                }
                else {
                    components.add(item.componentKey);
                }
                break;
            case 'attribute':
                if (!components.has(item.componentKey)) {
                    throw new Error(`Invalid item #${i + 1}. Component "${item.componentKey}" must go before any of its attribute`);
                }
                if (!attributes.has(item.componentKey)) {
                    attributes.set(item.componentKey, []);
                }
                attributes.get(item.componentKey).push({ attributeKey: item.attributeKey });
                break;
        }
    }
    for (const [componentKey, logicOrder] of attributes) {
        const componentType = type.components.find((component) => component.key === componentKey);
        if (!componentType) {
            throw new Error(`Invalid componentKey "${componentKey}"`);
        }
        try {
            validateProductLogicOrder(logicOrder, componentType?.type);
        }
        catch (e) {
            if (e instanceof Error) {
                throw new Error(`Invalid component "${componentKey}". ${e.message}`);
            }
            else {
                throw e;
            }
        }
    }
}
function getDefaultCompositeLogicOrder(type) {
    const result = [];
    for (const component of type.components) {
        const attributes = getDefaultProductLogicOrder(component.type);
        result.push({
            componentKey: component.key,
            type: 'availability',
        });
        for (const attribute of attributes) {
            result.push({
                componentKey: component.key,
                attributeKey: attribute.attributeKey,
                type: 'attribute',
            });
        }
    }
    return result;
}

const ComponentKey = 'product';
class BasicProductApiBuilder {
    options;
    fetcher;
    defaultValidationOptions;
    logicEngineBuilder = new LogicEngineBuilder();
    product;
    attributes = [];
    defaultProductVariant;
    productVariantsDto = [];
    logicOrder = [];
    isInCatalog;
    constructor(options, fetcher, defaultValidationOptions) {
        this.options = options;
        this.fetcher = fetcher;
        this.defaultValidationOptions = defaultValidationOptions;
        this.isInCatalog = 'catalogId' in options;
    }
    async load() {
        [this.product, this.defaultProductVariant] = await Promise.all([
            this.fetcher.getProductSchema(this.options),
            this.fetcher.getDefaultProductVariant(this.options),
        ]);
        this.parseAttributes();
        this.createSchema();
        const model = {
            [ComponentKey]: ConfiguratorToDomainProductMapper.toCfgProductVariant({
                variant: this.defaultProductVariant,
                componentKey: ComponentKey,
                product: this.product,
                model3dId: null,
            }).mapped,
        };
        const api = new BasicProductApi(this.defaultProductVariant.variantId, ComponentKey, this.product.productType.name, this.attributes, { model }, this.product.productName, this.defaultValidationOptions);
        api.injectLogicEngine(this.prepareEngine());
        return api;
    }
    parseAttributes() {
        if (this.product.logicOrder) {
            this.parseLogicOrder(this.product.logicOrder);
        }
        else {
            this.createImplicitLogicOrder();
        }
        this.attributes = this.logicOrder.map((item) => ({
            id: item.attributeKey,
            key: item.attributeKey,
        }));
    }
    createSchema() {
        this.logicEngineBuilder.setSchema({
            attributes: this.attributes.map((attribute) => ({
                attributeKey: attribute.key,
                componentKey: ComponentKey,
            })),
        });
    }
    async prepareEngine() {
        this.productVariantsDto = await this.fetcher.getProductVariants(this.options);
        this.createProductVariants();
        return this.logicEngineBuilder.build();
    }
    createProductVariants() {
        this.logicEngineBuilder.addProductVariants(ComponentKey, this.productVariantsDto.map((variant) => ConfiguratorToDomainProductMapper.toCfgProductVariant({
            variant,
            componentKey: ComponentKey,
            product: this.product,
            model3dId: null,
        }).mapped));
    }
    createImplicitLogicOrder() {
        this.logicOrder = getDefaultProductLogicOrder(this.product.productType);
    }
    parseLogicOrder(logicOrder) {
        this.logicOrder = logicOrder;
        this.validateLogicOrder();
    }
    validateLogicOrder() {
        validateProductLogicOrder(this.logicOrder, this.product.productType);
    }
}

class CompositeProductComponentApi {
    api;
    componentData;
    productComponent;
    awaitInjection = null;
    constructor(api, componentData, productComponent) {
        this.api = api;
        this.componentData = componentData;
        this.productComponent = productComponent;
    }
    get variant() {
        return this.api.variant;
    }
    getProductData() {
        if (this.variant.isNullish) {
            return null;
        }
        const productId = this.variant.productId;
        const result = this.componentData.products.find((it) => String(it.productId) === productId);
        if (!result) {
            throw new Error('Component data not found');
        }
        return result;
    }
    getKey() {
        return this.api.getKey();
    }
    injectAwait(awaitInjection) {
        this.awaitInjection = awaitInjection;
        this.api.injectLogicEngine(awaitInjection.then((value) => value.logicEngine));
    }
    getAttributeValue(attributeKey) {
        return this.api.getAttributeValue(attributeKey);
    }
    getAttributes(skipSystemAttributes = false) {
        return this.api
            .getAttributes()
            .filter((attribute) => attribute.key !== VariantProperties.available && (!skipSystemAttributes || !attribute.key.startsWith('_')));
    }
    getName() {
        return this.api.getName();
    }
    getPrice() {
        return this.api.getPrice();
    }
    getMetaId() {
        return this.api.getMetaId();
    }
    getVariant() {
        return this.api.getVariant();
    }
    isAvailable() {
        return this.api.isAvailable();
    }
    isEnabled() {
        return this.productComponent.enabled;
    }
    isIncludedInPdf() {
        return this.productComponent.includeInPdf;
    }
    async loadAvailability() {
        const values = await this.api.loadAttributeValues(VariantProperties.available);
        return values.map((item) => getAttributeValue(item));
    }
    loadCompatibleAttributeValues(attributeId) {
        return this.api.loadCompatibleAttributeValues(attributeId);
    }
    loadAllCompatibleAttributeValues() {
        return this.api.loadAllCompatibleAttributeValues();
    }
    loadAllAttributeValues(attributeId) {
        return this.api.loadAllAttributeValues(attributeId);
    }
    loadAllAttributesValues() {
        return this.api.loadAllAttributesValues();
    }
    setAttributeValue(attributeId, attributeValue) {
        return this.api.setAttributeValue(attributeId, attributeValue);
    }
    setAvailability(available) {
        return this.api.setAttributeValueByAttributeKey(VariantProperties.available, createAttributeValue(available));
    }
}

const INVALID_SAVED_CONFIGURATION = 'INVALID_SAVED_CONFIGURATION';
const INVALID_CONFIGURATION = 'INVALID_CONFIGURATION';
function isCatalogOptions(options) {
    return 'catalogId' in options;
}
class CompositeProductApiBuilder {
    options;
    fetcher;
    defaultValidationOptions;
    isInCatalog;
    compositeProduct;
    initialCompositeProductVariant;
    productComponentsApi = [];
    productComponentsApiByComponent = new Map();
    awaitLogicEngineReady;
    modelRef = { model: {} };
    compositeProductVariants;
    logicEngine;
    builder = new LogicEngineBuilder();
    attributesUsedInRules = new Set();
    attributes = [];
    logicOrder = [];
    variants = {};
    #productData = null;
    bomDetails = [];
    UIConfig = null;
    itemOrder = [];
    get productData() {
        if (!this.#productData) {
            throw new Error('Product data is not loaded');
        }
        return this.#productData;
    }
    constructor(options, fetcher, defaultValidationOptions) {
        this.options = options;
        this.fetcher = fetcher;
        this.defaultValidationOptions = defaultValidationOptions;
        this.isInCatalog = 'catalogId' in options;
    }
    async load(initialVariant) {
        [
            this.compositeProduct,
            this.initialCompositeProductVariant,
            this.bomDetails,
            this.UIConfig,
            this.compositeProductVariants,
        ] = await Promise.all([
            this.fetcher.getCompositeProductSchema(this.options),
            this.fetcher.getInitialCompositeProductVariant(this.options, initialVariant),
            isCatalogOptions(this.options) ? this.fetcher.getBomDetails(this.options) : Promise.resolve([]),
            this.fetcher.getCompositeUiSpecification(this.options),
            this.fetcher.getCompositeProductVariants(this.options),
        ]);
        this.#productData = await this.fetcher.getCompositeData(this.options);
        if (!this.initialCompositeProductVariant.components) {
            if (this.options.link) {
                throw new Error(INVALID_SAVED_CONFIGURATION);
            }
            else {
                throw new Error(INVALID_CONFIGURATION);
            }
        }
        this.parse();
        const awaitLogicEngineReady = this.prepareEngine();
        this.awaitLogicEngineReady = awaitLogicEngineReady;
        this.initModel();
        for (const componentApi of this.productComponentsApi) {
            const promise = awaitLogicEngineReady.then((logicEngine) => {
                const variants = this.variants[componentApi.getKey()];
                return {
                    logicEngine,
                    variants,
                };
            });
            componentApi.injectAwait(promise);
        }
        const compositeApi = new CompositeProductApi(this.options, this.fetcher, this.defaultValidationOptions, this.modelRef, this.compositeProduct, this.attributes, this.productComponentsApi, this.productComponentsApiByComponent, this.bomDetails, awaitLogicEngineReady.then((logicEngine) => ({
            logicEngine,
            compositeProductVariants: this.variants,
            rules: this.compositeProduct.rules,
        })), this.productData, this.itemOrder, this.compositeProductVariants);
        for (const componentApi of this.productComponentsApi) {
            const productApi = componentApi.api;
            productApi.setModelChangeNotifier((previousModel, newModel) => {
                compositeApi.notifyModelChange(previousModel, newModel);
            });
        }
        return compositeApi;
    }
    parse() {
        this.parseAttributes();
        this.parseProductComponentsApi();
        this.parseUIConfig();
    }
    parseAttributes() {
        if (this.compositeProduct.logicOrder) {
            this.parseLogicOrder(this.compositeProduct.logicOrder);
        }
        else {
            this.logicOrder = getDefaultCompositeLogicOrder(this.compositeProduct.productType);
        }
        this.attributes = this.logicOrder.map((attribute) => {
            const attributeKey = attribute.type === 'attribute'
                ? attribute.attributeKey
                : attribute.type === 'availability'
                    ? VariantProperties.available
                    : VariantProperties.productId;
            const componentKey = attribute.componentKey;
            const attributeId = createAttributeId(componentKey, attributeKey);
            return {
                componentKey,
                attributeKey,
                attributeId,
            };
        });
    }
    parseProductComponentsApi() {
        this.productComponentsApi = [];
        this.productComponentsApiByComponent.clear();
        const visitedComponentKeys = new Set();
        const components = this.logicOrder.filter((it) => it.type === 'availability');
        for (const component of components) {
            const componentKey = component.componentKey;
            if (!visitedComponentKeys.has(componentKey)) {
                visitedComponentKeys.add(componentKey);
                const type = this.getProductType(componentKey);
                const id = this.initialCompositeProductVariant.components[componentKey]?.variant.variantId;
                const api = new BasicProductApi(id, componentKey, type.name, this.getConfigurableAttributes(componentKey), this.modelRef, componentKey, this.defaultValidationOptions);
                api.injectLogicEngine(this.awaitLogicEngineReady);
                const componentSchema = this.compositeProduct.productType.components?.find((it) => it.key === componentKey);
                if (!componentSchema) {
                    throw new Error(`Cannot find component schema for component key "${componentKey}"`);
                }
                const productComponentImpl = new CompositeProductComponentApi(api, this.productData.components[componentKey], componentSchema);
                this.productComponentsApiByComponent.set(componentKey, productComponentImpl);
                this.productComponentsApi.push(productComponentImpl);
            }
        }
    }
    createSchema() {
        this.builder.setSchema({
            attributes: this.attributes.map((attribute) => ({
                attributeKey: attribute.attributeKey,
                componentKey: attribute.componentKey,
            })),
        });
    }
    async prepareEngine() {
        this.variants = this.createProductVariants();
        this.createRules();
        this.createSchema();
        this.initModel();
        return this.builder.build();
    }
    createProductVariants() {
        const variants = ConfiguratorToDomainProductMapper.toCfgCompositeVariants(this.compositeProductVariants);
        for (const [componentKey, componentVariants] of Object.entries(variants)) {
            this.builder.addProductVariants(componentKey, componentVariants);
        }
        return variants;
    }
    getProductType(componentKey) {
        const productComponent = this.compositeProduct.productType.components.find((component) => component.key === componentKey);
        if (!productComponent) {
            throw new Error(`Cannot find product type. Invalid component key "${componentKey}"`);
        }
        return productComponent.type;
    }
    createRules() {
        this.attributesUsedInRules.clear();
        for (const rule of this.compositeProduct.rules) {
            const componentKeys = Object.keys(rule.deps);
            for (const [componentKey, attributes] of Object.entries(rule.deps)) {
                for (const attributeKey of attributes) {
                    const id = this.createComponentAttributeId(attributeKey, componentKey);
                    this.attributesUsedInRules.add(id);
                }
            }
            this.builder.addStringRule({
                name: rule.name,
                deps: { componentKeys: componentKeys },
                fn: rule.fn,
            });
        }
    }
    getConfigurableAttributes(componentKey) {
        return this.attributes
            .filter((attribute) => attribute.componentKey === componentKey)
            .map((attribute) => ({
            id: attribute.attributeId,
            key: attribute.attributeKey,
        }));
    }
    createComponentAttributeId(attributeKey, componentKey) {
        return attributeKey + '_$$_' + componentKey;
    }
    initModel() {
        this.modelRef.model = ConfiguratorToDomainProductMapper.toCfgCompositeProduct(this.initialCompositeProductVariant, this.initialCompositeProductVariant?.custom);
    }
    getComponentApi(componentKey) {
        const api = this.productComponentsApiByComponent.get(componentKey);
        if (!api) {
            throw new Error(`Invalid component key "${componentKey}"`);
        }
        return api;
    }
    parseLogicOrder(logicOrder) {
        this.logicOrder = logicOrder;
        validateCompositeLogicOrder(this.logicOrder, this.compositeProduct.productType);
    }
    parseUIConfig() {
        this.itemOrder = [];
        const root = this.UIConfig?.layout?.layout;
        if (!root) {
            return;
        }
        const addItemToOrder = (item) => {
            const visible = Boolean(item.enabled && item.notAvailable !== true);
            if (item.kind === 'component') {
                this.itemOrder.push({
                    attributeKey: item.source.componentKey,
                    visible,
                    kind: 'component',
                    componentKey: item.source.componentKey,
                });
            }
            else {
                this.itemOrder.push({
                    attributeKey: item.source.attributeKey,
                    visible,
                    kind: item.kind,
                    componentKey: item.source.componentKey,
                });
            }
        };
        const traverse = (item) => {
            if (item.kind === 'component' || item.kind === 'product' || item.kind === 'attribute') {
                addItemToOrder(item);
            }
            if (item.kind === 'container') {
                item.children.forEach(traverse);
            }
        };
        traverse(root);
    }
}

class CatalogApiBuilder {
    catalogId;
    options;
    fetcher;
    defaultValidationOptions;
    constructor(catalogId, options, fetcher, defaultValidationOptions) {
        this.catalogId = catalogId;
        this.options = options;
        this.fetcher = fetcher;
        this.defaultValidationOptions = defaultValidationOptions;
    }
    async loadBasicProduct(productId) {
        const apiBuilder = new BasicProductApiBuilder({
            productId,
            projectId: this.options.projectId,
            ...(this.catalogId ? { catalogId: this.catalogId, version: this.options.version } : {}),
            version: this.options.version,
            configurationId: this.options.configurationId,
        }, this.fetcher, this.defaultValidationOptions);
        const api = await apiBuilder.load();
        return api;
    }
    async loadCompositeProduct(productId, initialVariant) {
        const apiBuilder = new CompositeProductApiBuilder({
            productId,
            projectId: this.options.projectId,
            ...(this.catalogId ? { catalogId: this.catalogId, version: this.options.version } : {}),
            version: this.options.version,
            link: this.options.link,
            configurationId: this.options.configurationId,
        }, this.fetcher, this.defaultValidationOptions);
        const api = await apiBuilder.load(initialVariant);
        return api;
    }
}

class ClpSdk {
    fetcher;
    constructor(fetcher) {
        this.fetcher = fetcher;
    }
    catalogId;
    options;
    defaultValidationOptions = {
        ignoreAllRules: false,
    };
    useCatalog(catalogId) {
        this.catalogId = catalogId;
        return this;
    }
    useApiOptions(options) {
        this.options = options;
        return this;
    }
    useDefaultValidationOptions(defaultValidationOptions) {
        this.defaultValidationOptions = defaultValidationOptions;
        return this;
    }
    build() {
        if (!this.options) {
            throw new Error('Options are required. Use useApiOptions() to set the options.');
        }
        return new CatalogApiBuilder(this.catalogId, this.options, this.fetcher, this.defaultValidationOptions);
    }
}

class HqrImageResolver {
    config;
    constructor(config) {
        this.config = config;
    }
    resolve(variant) {
        for (const entry of this.config) {
            if (entry.combination && entry.combination.length > 0) {
                if (this.allConditionsMatch(entry.combination, variant)) {
                    return entry.image;
                }
            }
        }
        const defaultEntry = this.config.find((e) => !e.combination || e.combination.length === 0);
        return defaultEntry?.image || null;
    }
    allConditionsMatch(conditions, variant) {
        return conditions.every((condition) => this.conditionMatches(condition, variant));
    }
    conditionMatches(condition, variant) {
        const componentVariant = variant[condition.componentKey];
        if (!componentVariant)
            return false;
        if (condition.type === 'attribute') {
            const attrValue = componentVariant[condition.attributeKey];
            if (!attrValue)
                return false;
            const actualValue = typeof attrValue === 'object' && 'value' in attrValue ? attrValue.value : attrValue;
            return actualValue === condition.value;
        }
        if (condition.type === 'product') {
            const productIdAttr = componentVariant['_productId'];
            if (!productIdAttr)
                return false;
            const productId = typeof productIdAttr === 'object' && 'value' in productIdAttr ? productIdAttr.value : productIdAttr;
            return String(productId) === String(condition.productId);
        }
        return false;
    }
    isEnabled() {
        return this.config.length > 0;
    }
}

export { CatalogApiBuilder, ClpSdk, CompositeProductApi, CompositeProductApiBuilder, CompositeProductComponentApi, Fetcher, HqrImageResolver, InvalidConfiguration, JsonFetcher };
//# sourceMappingURL=bundle.mjs.map
